/*
 * Budget component specific for Adalia M&E.
 */
import React from "react";
import Base from './base.jsx'; 
import { Mode, DataType, castDataType } from "../common.jsx";
import Helpers from './helpers.jsx'
import Setting from "./setting.jsx";
import Grid from "../../budgeting/GridContainer.jsx";
import { Button } from "../../bootstrap/index.jsx";
import { update } from "../../../helpers/index.js";
import { Adalicon } from "../../common/Icons.jsx";
import Tooltip from "../../common/Tooltip.jsx";
import { parseFormula, sortFormulasBasedOnDependencies } from "../../../helpers/index.js";
import Select from "../../common/Select.jsx";
import { Parser } from 'hot-formula-parser';

import { dimension_list_json_path } from '~/routes';

export default class AdaliaMeBudget extends Base {
  static get CATEGORY() {
    return "question";
  }

  static get TYPE() {
    return "adalia_me_budget";
  }

  static get COLUMN_TYPE() {
    return {
      INPUT: "input",
      CALCULATION: "calculation",
      TEXT: "text"
    };
  }

  static icon() {
    return <i className="fa fa-table"></i>;
  }

  static get TRANSLATED_ATTRIBUTES() {
    return {
      label: "field_default_label",
      description: "field_default_description",
    };
  }

  static defaultContent(language) {
    const colLabel = this.generateColumnLabelKey();

    return Object.assign(super.defaultContent(language), {
      indicator: {},
      label: "label",
      dataType: DataType.INTEGER,
      description: "description",
      name: "field_" + Helpers.randomString(),
      columns: [
        {
          key: "a",
          type: this.COLUMN_TYPE.INPUT,
          label: colLabel,
        },
      ],
      translations: {
        [language]: {
          ...this.defaultContentTranslations(language)[language],
          [colLabel]: I18n.t("total_default_label", { scope: "fe", language }),
        },
      },
      prettyView: {
          hide: false,
          show_label: false
      }
    });
  }

  static generateColumnLabelKey() {
    return "col-" + Helpers.randomString(12);
  }

  constructor(props) {
    super(props);

    this.state = {
      gridKey: Helpers.randomString(12),
      dimensions: []
    };

    this.columnsRef = React.createRef();
  }

  getOptionsForComponentSelect() {
    const { content } = this.props;
    return (content.columns || []).map((c) => [`${content.id}_${c.label}`, `${this.getText(content.label)} - ${this.getText(c.label)}`]);
  }

  componentDidMount() {
    super.componentDidMount();
    if (this.props.pdf) return;

    if (this.props.mode == Mode.SETTINGS) {
      jQuery(this.columnsRef.current).sortable({
        axis: "y",
        handle: ".sort-handle",
        update: this.updateColumnsSortOrder,
      });
    }
    jQuery.getJSON(dimension_list_json_path({ use_in: ["budgeting"] }), (data) => {
      // Change dimension ids from number to string, because after form data serialization(in updateSettings) value will be string. 
      const dimensions =  data.map(dm => ({ ...dm, id: dm.id.toString() })).map((d) => ({
        ...d,
        categories: d.categories.flatMap((c) => [c, ...c.categories]),
      }));
      this.setState({
        dimensions: dimensions
      })
    });
  }

  renderView() {
    if (this.props.pdf){
      return (
        <div style={{ display: "flex", flexDirection: "column" }}>
        <label>
          {this.getText("label")}
        </label>
        {this.descriptionText()}
        <div className='non-printable-component text-muted'>{I18n.t('non_printable_component_placeholder')}</div>
        </div>
      )
    }
    return (
      <div style={{ display: "flex", flexDirection: "column" }}>
        <label className={this.props.content.label_hidden ? 'hidden-label': ''}>
          <span className="field-label">{this.getText('label')}</span>
          {this.renderPrefillIcon()}
        </label>
        {this.descriptionText()}

        <div className="col-xs-12">
          <Grid
            key={this.state.gridKey}
            rows={this.getAnswer()}
            columns={(this.props.content.columns || []).map((c) => ({
              ...c,
              label: this.getText(c.label),
              strId: c.label,
            }))}
            readOnly={this.props.mode != Mode.FILL}
            onUpdate={this.updateAnswer}
            fullWidth={false}
            formMode={this.props.mode}
            showSummaryTable={this.props.content.summaryTable}
            calculateAggregateColumnFormula={this.calculateAggregateColumnFormula}
            stateKey={this.fullFieldName()}
            dimensions={this.props.content.visibleDimensions ? this.state.dimensions.filter((d) => this.props.content.visibleDimensions.includes(d.id)) : this.state.dimensions}
            selectedDimensions={this.props.content.groupBy || []}
            groupByColumn={this.props.groupByColumn}
          />
        </div>

        <input type="hidden" value={this.getRawAnswer()} name={this.fullFieldName()} />

        {(this.props.content.columns || []).map((c) => (
          // Input for each column total value
          <input
            key={c.label}
            type="hidden"
            name={this.fullFieldName(false, `.${c.label}`)}
            value={this.getAggregateAnswer(c.label)}
          />
        ))}

        {this.props.mode == Mode.FILL && this.hasPrefilledAnswer() ? (
          // Initialize a hidden Grid which will provide the answer using prefill answer
          // It will be used the check whether prefill value is matching current value
          <Grid
            hidden
            rows={JSON.parse(this.getPrefilledAnswer())}
            columns={(this.props.content.columns || []).map((c) => ({
              ...c,
              label: this.getText(c.label),
              strId: c.label,
            }))}
            readOnly
            afterInit={this.rowsInitialized}
            calculateAggregateColumnFormula={this.calculateAggregateColumnFormula}
          />
        ) : null}
      </div>
    );
  }

  renderPrettyView() {
    if (this.props.pdf){
      return (
        <div className='non-printable-component text-muted'>{I18n.t('non_printable_component_placeholder')}</div>
      )
    }
    const { hide = false, show_label = false } = this.props.content.prettyView || {};
    return ( hide ? null :
      <>
        {show_label &&
          <label>
              <span className="field-label">{this.getText('label')}</span>
          </label>
        }
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div className="col-xs-12">
            <Grid
              key={this.state.gridKey}
              rows={this.getAnswer()}
              columns={(this.props.content.columns || []).map((c) => ({
                ...c,
                label: this.getText(c.label),
                strId: c.label,
              }))}
              readOnly={this.props.mode != Mode.FILL}
              onUpdate={this.updateAnswer}
              fullWidth={false}
              formMode={this.props.mode}
              showSummaryTable={this.props.content.summaryTable}
              calculateAggregateColumnFormula={this.calculateAggregateColumnFormula}
              stateKey={this.fullFieldName()}
              dimensions={this.props.content.visibleDimensions ? this.state.dimensions.filter((d) => this.props.content.visibleDimensions.includes(d.id)) : this.state.dimensions}
              selectedDimensions={this.props.content.groupBy || []}
              groupByColumn={this.props.groupByColumn}
            />
          </div>

          <input type="hidden" value={this.getRawAnswer()} name={this.fullFieldName()} />

          {(this.props.content.columns || []).map((c) => (
            // Input for each column total value
            <input
              key={c.label}
              type="hidden"
              name={this.fullFieldName(false, `.${c.label}`)}
              value={this.getAggregateAnswer(c.label)}
            />
          ))}
        </div>
      </>
    );
  }

  rowsInitialized = (rows) => {
    this.setState({ rawValueAfterInit: JSON.stringify(rows) });
  }

  updateAnswer(rows) {
    if (this.props.mode == Mode.FILL) {
      super.updateAnswer(JSON.stringify(rows));
    }
  }

  getAnswer() {
    return this.getRawAnswer() ? JSON.parse(this.getRawAnswer()) : [];
  }

  getColumns(type) {
    return (this.props.content.columns || []).filter((c) => c.type == type);
  }

  getAggregateAnswer(col, context) {
    const inputCol = this.getColumns(this.constructor.COLUMN_TYPE.INPUT).find((c) => c.label == col);
    if (inputCol) {
      return this.getAnswer(context).reduce((acc, val) => (acc += val[col] ? parseFloat(val[col], 10) : 0), 0);
    }

    const calcCol = this.getColumns(this.constructor.COLUMN_TYPE.CALCULATION).find((c) => c.label == col);
    if (calcCol) {
      return this.calculateAggregateColumnFormula(calcCol, this.getAnswer(context) || []);
    }

    return this.rawAnswerDefault();
  }

  getFormattedPrefillAnswer() {
    return I18n.t("x_rows", { scope: "fe", rows: JSON.parse(this.getPrefilledAnswer()).length });
  }

  getPrefilledAnswer() {
    const prefilledAnswer = super.getPrefilledAnswer();

    if (prefilledAnswer) {
      return JSON.stringify(
        JSON.parse(prefilledAnswer).map((row) => {
          this.getColumns(this.constructor.COLUMN_TYPE.INPUT).forEach((col) => (row[col.label] = row[col.key]));
          return row;
        })
      );
    } else {
      return prefilledAnswer;
    }
  }

  usePrefilledValue() {
    super.usePrefilledValue();

    // Refresh the grid
    this.setState({ gridKey: Helpers.randomString(12) });
  }

  mayUsePrefillAnswer() {
    return this.state.rawValueAfterInit != this.getRawAnswer();
  }

  calculateAggregateColumnFormula = (col, leafValues) => {
    let variableValues = Object.fromEntries(
      this.getColumns(this.constructor.COLUMN_TYPE.INPUT)
        .filter((c) => c.key)
        .map((c) => [c.key, leafValues.reduce((sum, row) => ((row ? castDataType(row[c.label], DataType.DECIMAL) : 0) || 0) + sum, 0)])
    );

    let errors = {};

    sortFormulasBasedOnDependencies(Parser, this.props.content.columns || [])
      .filter((c) => c.formula)
      .forEach((c) => {
        const { result, error } = parseFormula(Parser, c.formula, variableValues);
        variableValues[c.key] = result;
        errors[c.key] = error;
      });

    return errors[col.key] || variableValues[col.key];
  };

  updateSettingsSelection(fieldName, newValue) {
    this.props.updateSettingsCallback(
      this.props.content.id,
      update(this.props.content, {
        [fieldName]: { $set: newValue }
      })
    );
  }

  renderSettings() {
    return (
      <div>
        <Setting.TextField name="label" label={I18n.t('field_label', {scope: 'fe'})} value={this.getText("label")} onChange={this.updateSettings} />
        <Setting.Checkbox name="label_hidden" className="label-hidden-check" label={I18n.t('field_label_hide', {scope: 'fe'})} value={this.props.content.label_hidden} onChange={this.updateSettings} />
        <Setting.LongTextField name="description" label={I18n.t('field_description', {scope: 'fe'})} value={this.getText("description")} onChange={this.updateSettings} />
        <Setting.TextField
          name="summaryTable"
          label={I18n.t('field_summary_table', {scope: 'fe'})}
          value={this.props.content.summaryTable}
          onChange={this.updateSettings}
          description={I18n.t('field_summary_table_description', {scope: 'fe'})}
        />
        <Setting.TextField
          name="reportKey"
          label={I18n.t('field_report_key', {scope: 'fe'})}
          value={this.props.content.reportKey}
          description={I18n.t('field_report_key_description', {scope: 'fe'})}
          componentType={this.constructor.TYPE}
          {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
        />
        <label className="w-100">{I18n.t('field_visible_dimensions', {scope: 'fe'})}</label>
        <Select
          name="visibleDimensions"
          value={this.props.content.visibleDimensions || this.state.dimensions.map(d => d.id)}
          isMulti={true}
          options={this.state.dimensions.map(d => Object.assign(d, {'value': d.id, 'label': d.name}))}
          onChange={(e) => this.updateSettingsSelection('visibleDimensions', e)}
          description={I18n.t('field_group_by_description', {scope: 'fe'})}
          autoComplete={this.getAutoComplete()}
        />
        <label className="w-100 mt-1">{I18n.t('field_summary_row_group_by', {scope: 'fe'})}</label>
        <Select
          name="groupBy"
          defaultValue={this.props.content.groupBy}
          isMulti={true}
          options={(this.props.content.visibleDimensions ? this.state.dimensions.filter((d) => this.props.content.visibleDimensions.includes(d.id)) : this.state.dimensions).map(d => Object.assign(d, {'value': d.id, 'label': d.name}))}
          onChange={(e) => this.updateSettingsSelection('groupBy', e)}
          description={I18n.t('field_group_by_description', {scope: 'fe'})}
          autoComplete={this.getAutoComplete()}
        />
        {this.renderColumnSettings()}
        {this.renderPrettyViewSettings()}
      </div>
    );
  }

  addColumn = (type) => {
    this.props.updateSettingsCallback(
      this.props.content.id,
      update(this.props.content, {
        columns: {
          $autoArray: (arr) => {
            let i = 9;

            return arr.concat({
              type,
              label: this.constructor.generateColumnLabelKey(),
              key: [...Array(26)].map((_) => (++i).toString(36)).join``[arr.length],
            });
          },
        },
      })
    );
  };

  deleteColumn = (idx) => {
    if (confirm(I18n.t("confirm_delete_column", { scope: "fe" }))) {
      this.props.updateSettingsCallback(
        this.props.content.id,
        update(this.props.content, {
          columns: {
            $splice: [[idx, 1]],
          },
        })
      );
    }
  };

  renderColumnSettings() {
    return (
      <>
        <div className="setting">
          <label>{I18n.t("columns", { scope: "fe" })}</label>
          <div className="d-flex">
            <div className="flex-1" style={{ marginLeft: "17px" }}>
              {I18n.t("field_report_key", { scope: "fe" })}
            </div>
            <div className="flex-2 pl-1">
              {I18n.t("column_label_placeholder", { scope: "fe" })} / ({I18n.t("field_formula", { scope: "fe" })})
            </div>
            <div className="flex-1 pl-1" style={{ marginRight: "44px" }}>
              {I18n.t("budget_column_value_label", { scope: "fe" })}
            </div>
          </div>
          <div ref={this.columnsRef}>
            {(this.props.content.columns || []).map((c, i) => (
              <div key={c.label} className="d-flex mb-1" data-sort-order={i}>
                <input type="hidden" name={`columns|${i}|type`} defaultValue={c.type} />
                <Helpers.SortHandle />
                {c.type != this.constructor.COLUMN_TYPE.TEXT ? (
                  <div className="flex-1">
                    <Tooltip title={I18n.t("budget_columns_key_help", { scope: "fe" })} options={{ placement: "right" }}>
                      <input type="text" name={`columns|${i}|key`} className="form-control" placeholder={I18n.t("field_report_key", { scope: "fe" })} defaultValue={c.key} onChange={this.updateSettings} autoComplete={this.getAutoComplete()} />
                    </Tooltip>
                  </div>
                ) : null}
                <div className="flex-2 pl-1">
                  <input type="hidden" name={`columns|${i}|label`} defaultValue={c.label} />
                  <input type="text" name={`columns|${i}|translated_label`} className="form-control" placeholder={I18n.t("column_label_placeholder", { scope: "fe" })} defaultValue={this.getText(c.label)} onChange={this.updateSettings} autoComplete={this.getAutoComplete()} />
                </div>
                {c.type == this.constructor.COLUMN_TYPE.CALCULATION ? (
                  <div className="flex-2 pl-1">
                    <input type="text" name={`columns|${i}|formula`} className="form-control" placeholder={I18n.t("field_formula", { scope: "fe" })} defaultValue={c.formula} onChange={this.updateSettings} autoComplete={this.getAutoComplete()} />
                  </div>
                ) : null}
                <div className="flex-1 pl-1">
                  <Tooltip title={I18n.t("budget_columns_value_help", { scope: "fe" })}>
                    <input type="text" name={`columns|${i}|value`} className="form-control" placeholder={I18n.t("budget_column_value_label", { scope: "fe" })} defaultValue={c.value} onChange={this.updateSettings} autoComplete={this.getAutoComplete()} />
                  </Tooltip>
                </div>
                <Button type="danger" elemType="button" size="sm" className="ml-1" onClick={() => this.deleteColumn(i)}>
                  <Adalicon type="trash" />
                </Button>
              </div>
            ))}
            <Button elemType="button" onClick={() => this.addColumn(this.constructor.COLUMN_TYPE.INPUT)}>
              <Adalicon type="add" /> {I18n.t("add_input_column", { scope: "fe" })}
            </Button>
            <Button elemType="button" onClick={() => this.addColumn(this.constructor.COLUMN_TYPE.CALCULATION)} className="ml-1">
              <Adalicon type="add" /> {I18n.t("add_calculation_column", { scope: "fe" })}
            </Button>
            <Button elemType="button" onClick={() => this.addColumn(this.constructor.COLUMN_TYPE.TEXT)} className="ml-1">
              <Adalicon type="add" /> {I18n.t("add_text_column", { scope: "fe" })}
            </Button>
          </div>
        </div>
      </>
    );
  }

  updateColumnsSortOrder = (event) => {
    let sortContainer = jQuery(event.target);
    let oldColumns = this.props.content.columns;
    let newColumns = [];
    sortContainer.sortable("toArray", { attribute: "data-sort-order" }).forEach((i) => {
      const col = oldColumns[parseInt(i, 10)];
      if (col) {
        newColumns.push({ ...col });
      }
    });

    let content = { id: this.props.content.id, columns: newColumns };

    this.props.updateSettingsCallback(content.id, content, true);
  };

  beforeUpdateSettings(content) {
    content.columns = [];

    // Delete old label translations
    content.translations[this.props.language] = content.translations[this.props.language] || {};
    Object.keys(content.translations[this.props.language]).forEach((k) => {
      if (k.match(/^col\-/)) {
        delete content.translations[this.props.language][k];
      }
    });

    Object.keys(content)
      .filter((k) => {
        return k.match(/^columns\|/);
      })
      .forEach((k) => {
        let parts = k.split("|"); // columns|index|attr
        let oIndex = parseInt(parts[1], 10);

        if (parts[2] == "translated_label") {
          content.translations[this.props.language][content[k.replace(/translated_label$/, "label")] || content[parts[0]][oIndex].label] = content[k];
        } else {
          content[parts[0]][oIndex] = content[parts[0]][oIndex] || {};
          content[parts[0]][oIndex][parts[2]] = content[k];
        }

        delete content[k];
      });
    content.label_hidden = castDataType(content.label_hidden, DataType.BOOLEAN);
    content.visibleDimensions = jQuery(this.refs.root).find('input[name="visibleDimensions"]').map(((i, e) => e.value)).get();
    content.groupBy = jQuery(this.refs.root).find('input[name="groupBy"]').map(((i, e) => e.value)).get();
    this.beforeUpdateSettingsPrettyView(content);
  }
}
